// src/app/borrower-dashboard/borrower-dashboard.component.ts

import { Component, OnInit, TemplateRef, ViewChild, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { ApplicationService } from '../../services/application.service';
import { InvitationService } from '../../services/invitation.service';
import { PlaidService } from '../../services/plaid.service';  // <-- import your new service
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ThankYouOwner } from '../../models/thank-you-owner.model';
import usStates from '../../shared/data/us-states.json';
import { InvitationCommunicationService } from '../../services/invitation-communication.service';
declare global {
  interface Window {
    Plaid: any;  // make TS happy that window.Plaid exists
  }
}

@Component({
  selector: 'app-borrower-dashboard',
  templateUrl: './borrower-dashboard.component.html',
  styleUrls: ['./borrower-dashboard.component.scss']
})
export class BorrowerDashboardComponent implements OnInit {
  @ViewChild('tosDialog') tosDialog!: TemplateRef<any>;

  borrowerForm: FormGroup;
  tosAcceptedControl: FormControl; // Separate FormControl for TOS
  isLoading: boolean = false;
  errorMessage!: string;
  maxOwners: number = 4;
  @Input() invitationToken: string = ''; // Renamed to invitationToken
  hasScrolledToBottom: boolean = false;
  plaidHandler: any;
  showThankYou: boolean = false;
  firstOwner!: ThankYouOwner;
  bankConnected: boolean = false; // Flag to check if Plaid linking is complete
  ssnMasked: boolean = true; // Whether or not the ssn is masked
  ssnReplacePattern = {'X': {pattern: new RegExp('\\d'), symbol: '●'}};

  currentDialogRef: MatDialogRef<any> | null = null; // Added to store dialog reference

  /** List of all US States for the State fields */
  public readonly usStates: string[] = usStates;

  constructor(
    private fb: FormBuilder,
    private applicationService: ApplicationService,
    private invitationService: InvitationService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private router: Router,
    private plaidService: PlaidService,  // <-- inject PlaidService
    private commService: InvitationCommunicationService,
  ) {
    // Initialize the separate FormControl for TOS acceptance
    this.tosAcceptedControl = new FormControl(false, Validators.requiredTrue);

    // Initialize the main borrowerForm without tosAccepted
    this.borrowerForm = this.fb.group({
      // Business Address Fields
      streetAddress: ['', Validators.required],
      suiteUnitOffice: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      // Business Details Fields
      businessLegalName: ['', Validators.required],
      businessDBA: [''],
      businessEmail: ['', [Validators.required, Validators.email]],
      businessPhone: [
        '',
        [Validators.required, Validators.pattern(/^\d{10}$/)]
      ],
      businessWebsite: ['',
        Validators.pattern(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/)],
      businessStructure: ['', Validators.required],
      businessTaxID: [
        '',
        [Validators.required, Validators.pattern(/^\d{2}-?\d{8}$/)]
      ],
      industryType: ['', Validators.required],
      yearsInBusiness: ['', Validators.required],
      numberOfEmployees: ['', [Validators.required, Validators.min(1)]],
      grossAnnualRevenue: ['', [Validators.required, Validators.min(1), Validators.max(10**16 - 1)]],
      referralNumber: ['', [Validators.maxLength(15)]],
      // Owners
      owners: this.fb.array([]),
      noIndividualOver25: [false],
      // Removed tosAccepted from borrowerForm
    });
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.invitationToken = this.route.snapshot.paramMap.get('token') || '';

    // Subscribe to 'noIndividualOver25' value changes
    this.borrowerForm.get('noIndividualOver25')?.valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        // Remove all owners if checkbox is checked
        // while (this.owners.length !== 0) {
        //   this.owners.removeAt(0);
        // }
      } else {
        // Add an owner if none exist
        if (this.owners.length === 0) {
          this.addOwner();
        }
      }
    });

    // Initialize owners if 'noIndividualOver25' is unchecked
    if (!this.borrowerForm.get('noIndividualOver25')?.value) {
      this.addOwner();
    }

    // Handle businessTaxID validators based on businessStructure
    this.borrowerForm.get('businessStructure')?.valueChanges.subscribe((businessStructure: string) => {
      if (businessStructure === 'SOLE_PROPRIETORSHIP') {
        this.borrowerForm.get('businessTaxID')?.clearValidators();
        this.borrowerForm.get('businessTaxID')?.updateValueAndValidity();
      } else {
        this.borrowerForm.get('businessTaxID')?.setValidators([
          Validators.required,
          Validators.pattern(/^\d{2}-?\d{8}$/)
        ]);
        this.borrowerForm.get('businessTaxID')?.updateValueAndValidity();
      }
    });

    // Initialize businessTaxID validators based on initial value
    const initialBusinessStructure = this.borrowerForm.get('businessStructure')?.value;
    if (initialBusinessStructure === 'SOLE_PROPRIETORSHIP') {
      this.borrowerForm.get('businessTaxID')?.clearValidators();
      this.borrowerForm.get('businessTaxID')?.updateValueAndValidity();
    }

    // Subscribe to tosAcceptedControl value changes for debugging
    this.tosAcceptedControl.valueChanges.subscribe((value: boolean) => {
      console.log('TOS Accepted Control Value:', value);
    });
  }

    // Step 1: Call the server for a Link Token, then set up the Plaid handler
    initPlaidLink(): void {
      this.plaidService.createLinkToken().subscribe({
        next: (resp) => {
          // Create the Plaid Link Handler
          this.plaidHandler = window.Plaid.create({
            token: resp.link_token,
            onSuccess: (public_token: string, metadata: any) => {
              this.plaidService.exchangePublicToken(public_token, this.commService.applicationGuid).subscribe({
                next: (exchangeResp) => {
                  console.log('Token exchange success', exchangeResp);
                  // Set the flag to show the success message
                  this.bankConnected = true;
                },
                error: (err) => {
                  console.error('Error exchanging public token', err);
                  this.snackBar.open('Error exchanging Plaid public token', 'Close', { duration: 3000 });
                }
              });
            },            
            onExit: (err: any, metadata: any) => {
              if (err != null) {
                console.error('Plaid Link encountered an error', err);
                // Show error message or handle accordingly
              }
            },
            onEvent: (eventName: any, metadata: any) => {
              // Optionally capture Link flow events
            },
            forceIframe: true,   // <--- Add this line
          });
  
          // Finally, open the Plaid Link widget
          this.plaidHandler.open();
        },
        error: (err) => {
          console.error('Error creating link token', err);
          this.snackBar.open('Error creating Plaid link token', 'Close', { duration: 3000 });
        }
      });
    }
  

  // Getter for owners FormArray
  get owners(): FormArray {
    return this.borrowerForm.get('owners') as FormArray;
  }

  // Getter for isSoleProprietorship
  get isSoleProprietorship(): boolean {
    return this.borrowerForm.get('businessStructure')?.value === 'SOLE_PROPRIETORSHIP';
  }

  // Getter for isNoIndividualOver25
  get isNoIndividualOver25(): boolean {
    return this.borrowerForm.get('noIndividualOver25')?.value;
  }

  // Handle number-only input
  onlyNumberKey(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // Handle scrolling in TOS dialog
  onTosScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollThreshold = target.scrollHeight - 10; // Adjusted threshold

    // Debugging: Log the scroll position and threshold
    console.log('Scroll Position:', scrollPosition, 'Scroll Threshold:', scrollThreshold);

    this.hasScrolledToBottom = scrollPosition >= scrollThreshold;

    // Debugging: Log the updated hasScrolledToBottom value
    console.log('Has Scrolled To Bottom:', this.hasScrolledToBottom);
  }

  // **Updated**: Open Terms of Service Dialog and store dialogRef
  openTosDialog(): void {
    this.hasScrolledToBottom = false; // Reset scroll state
    this.tosAcceptedControl.reset(); // Reset TOS acceptance

    // Open the dialog and store the reference
    this.currentDialogRef = this.dialog.open(this.tosDialog, {
      width: '600px', // Optional: Set a width for better UX
    });

    // Subscribe to afterClosed to handle the confirmation
    this.currentDialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.onSubmit(); // Submit the form
      }
      this.currentDialogRef = null; // Reset the dialog reference
    });
  }

  // **Updated**: Confirm TOS and pass confirmation
  confirmTos(): void {
    if (this.currentDialogRef) {
      this.currentDialogRef.close(true); // Pass 'true' to indicate confirmation
    }
  }

  // Add an owner to the form
  addOwner(owner?: any): void {
    if (this.owners.length >= this.maxOwners) {
      this.snackBar.open(`You can only add up to ${this.maxOwners} owners.`, 'Close', {
        duration: 3000,
      });
      return;
    }

    this.owners.push(this.createOwnerFormGroup(owner));
    // this.snackBar.open('Owner added successfully.', 'Close', {
    //   duration: 2000,
    // });
  }

  // Remove an owner from the form
  removeOwner(index: number): void {
    if (this.owners.length > 1) {
      this.owners.removeAt(index);
      this.snackBar.open('Owner removed successfully.', 'Close', {
        duration: 2000,
      });
    } else {
      this.snackBar.open('At least one owner is required.', 'Close', {
        duration: 3000,
      });
    }
  }

  // Create a FormGroup for an owner
  createOwnerFormGroup(owner?: any): FormGroup {
    return this.fb.group({
      id: [owner?.id || null],
      firstName: [owner?.firstName || '', Validators.required],
      middleInitial: [owner?.middleInitial || ''],
      lastName: [owner?.lastName || '', Validators.required],
      emailAddress: [
        owner?.emailAddress || '',
        [Validators.required, Validators.email]
      ],
      phoneNumber: [
        owner?.phoneNumber || '',
        [Validators.required, Validators.pattern(/^\d{10}$/)]
      ],
      dateOfBirth: [owner?.dateOfBirth || '', Validators.required],
      ssn: [
        owner?.ssn || '',
        [Validators.required]
      ],
      streetAddress: [owner?.streetAddress || '', Validators.required],
      suiteUnitOffice: [owner?.suiteUnitOffice || ''],
      city: [owner?.city || '', Validators.required],
      state: [owner?.state || '', Validators.required],
      zipCode: [
        owner?.zipCode || '',
        [Validators.required, Validators.pattern(/^\d{5}$/)]
      ],
    });
  }

  // Submit the form
  onSubmit(): void {
    if (this.isLoading) return;

        // Check if bank is connected
        if (!this.bankConnected) {
          this.snackBar.open('Please connect your bank via Plaid before submitting.', 'Close', { duration: 3000 });
          return;
        }
  
    if (this.borrowerForm.valid) {
      if (this.tosAcceptedControl.valid && this.hasScrolledToBottom) {
        this.isLoading = true;
        const updatedApplication = this.borrowerForm.value;
        updatedApplication.businessTaxID = updatedApplication.businessTaxID?.replace(/-/g, ''); // Remove dashes from businessTaxID
        updatedApplication.invitationToken = this.invitationToken;
        updatedApplication.applicationGuid = this.commService.applicationGuid;
        
        // First, update the application
        this.applicationService.updateApplication('borrower', updatedApplication).subscribe({
          next: (appResponse) => {
            // Happy path - application updated and invitation accepted
            const ownerData = updatedApplication.owners[0];
            if (ownerData) {
              this.firstOwner = {
                firstName: ownerData.firstName,
                lastName: ownerData.lastName,
                emailAddress: ownerData.emailAddress
              };
            }

            this.isLoading = false;
            this.snackBar.open('Application updated and invitation accepted successfully!', 'Close', { duration: 3000 });
            
            // Instead of redirecting, show the thank-you content:
            this.showThankYou = true;
          },
          error: (invError) => {
            // Could not update application or accept invitation
            this.isLoading = false;
            this.snackBar.open('An error occurred while updating your application.', 'Close', { duration: 3000 });
          }
        });
      } else {
        this.snackBar.open('Please accept the Terms of Service to proceed.', 'Close', { duration: 3000 });
        this.openTosDialog();
      }
    } else {
      this.borrowerForm.markAllAsTouched();
      this.snackBar.open('Please correct the errors in the form before submitting.', 'Close', { duration: 3000 });
    }
  }
  
  toggleSSNVisibility(): void {
    this.ssnMasked = !this.ssnMasked;
  }
}
